import {
    Component,
    ChangeDetectionStrategy,
    OnDestroy,
    ViewChild,
    AfterViewInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { IconComponent } from '@neutrino/core/icons';
import { SlideService } from '@neutrino/core/slide';
import { fortinetIcon } from '@neutrino/ftnt-icons/brands';
import { Observable, Subject, combineLatest, firstValueFrom } from 'rxjs';
import { filter, map, shareReplay, switchMap, takeUntil } from 'rxjs/operators';
import logger from 'loglevel';
import {
    headsetIcon,
    listOlIcon,
    toolboxIcon,
    stethoscopeIcon,
    microscopeIcon,
    shieldIcon,
    infoIcon,
    rightFromBracketIcon,
    userIcon,
    ticketSimpleIcon
} from '@neutrino/fa-icons/solid';
import {
    SectionNavEntry,
    EndpointNavEntry,
    ThemeService,
    NavComponent,
    NavGroupMeta,
    NavNotifyLevel
} from '@neutrino/core/app-shell';
import { compileTheme } from '@neutrino/theme-compiler';
import { PromptService } from '@neutrino/core/prompts';
import { Persist } from '@neutrino/core/common';

import { fullClientRoutes } from '@flb/common/client-routes';
import { truthy } from '@flb/common/util';
import { EventMode } from '@flb/common/event-mode';
import { AccessLevel } from '@flb/common/access-level';
import { AnySupportRequestUpdate, SupportRequestCategory } from '@flb/common/support-request';

import { AppHeaderService } from './app-header.service.js';
import { leaderboardTheme } from './leaderboard-theme.js';
import { NavSectionService } from './nav-section.service.js';
import { ClientStateService } from './common/client-state.service.js';
import { StateStreamService } from './common/state-stream.service.js';
import { EventModePromptComponent } from './common/event-mode-prompt/event-mode-prompt.component.js';
import { LeaderboardClientConfigService } from './common/leaderboard-client-config.service.js';
import { APP_SHELL_DIRECTIVES } from './common/app-shell-directives.js';
import { SupportRequestNotificationService } from './support-request/support-request-notification.service.js';

type AnyNavEntry = SectionNavEntry | EndpointNavEntry;

const DEFAULT_NAV_FAVORITES = [
    'Admin Panel',
    'Team Summary',
    'Team Debug',
    'Storyboard',
    'Targets',
    'Logs'
];
const SUPPORT_REQUEST_SEVERITY: Record<string, NavNotifyLevel> = {
    [SupportRequestCategory.Objective]: NavNotifyLevel.Medium,
    [SupportRequestCategory.Devices]: NavNotifyLevel.Critical,
    [SupportRequestCategory.Bug]: NavNotifyLevel.Medium,
    [SupportRequestCategory.GameClient]: NavNotifyLevel.Medium,
    [SupportRequestCategory.Networking]: NavNotifyLevel.Critical,
    [SupportRequestCategory.Other]: NavNotifyLevel.Low
};
@Component({
    selector: 'flb-root',
    template: `
        <nu-header *ngIf="appHeaderService.headerVisible | async">
            <ng-template nuHeaderPrimaryLabel>
                <div class="nu-flex-centered">
                    <nu-icon [icon]="appIcon"></nu-icon>
                    <span>{{ title }}</span>
                </div>
            </ng-template>
            <ng-template nuHeaderInfo>
                <img src="/assets/images/hud-line.svg" />
            </ng-template>
            <ng-template nuHeaderActions>
                <nu-header-action (click)="logOut()">
                    <nu-icon [icon]="logOutIcon"></nu-icon>Log Out
                </nu-header-action>
            </ng-template>
        </nu-header>
        <nu-nav
            [items]="navEntries"
            [favoriteGroup]="'favorite'"
            [groupMetaPersist]="groupPersist"
            [withFavorite]="true"
            [favoritePersist]="persist"
            [rebuild]="rebuild"
            [withNotification]="true"
            [hideParentNotificationWhenExpanded]="true"
            #nav
        ></nu-nav>
        <nu-router-outlet></nu-router-outlet>
    `,
    styleUrls: ['app.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, IconComponent, APP_SHELL_DIRECTIVES],
    standalone: true
})
export class AppComponent implements AfterViewInit, OnDestroy {
    readonly title = 'Fortinet Fabric Challenge';
    readonly appIcon = fortinetIcon;
    // TODO: why does this icon not work
    readonly logOutIcon = rightFromBracketIcon;
    private readonly destroyed = new Subject<void>();
    private nav = new Subject<NavComponent>();

    @ViewChild('nav')
    protected set setNav(value: NavComponent) {
        this.nav.next(value);
    }

    rebuild: Observable<void>;

    groupsMeta = new Map<string, NavGroupMeta>([
        [
            'favorite',
            {
                reorderable: true
            }
        ]
    ]);

    groupPersist = {
        save: (value: Map<string, NavGroupMeta>) => {
            this.groupsMeta = value;
            return Promise.resolve();
        },
        load: () => {
            return Promise.resolve(this.groupsMeta);
        }
    };

    navEntries: Observable<Array<AnyNavEntry>>;

    modePrompted = false;

    persist: Persist<string[]> = {
        // Artificial delay between favoriting something and seeing the favorite added
        save: async (values: string[]) => {
            await new Promise<void>(function (resolve) {
                setTimeout(() => resolve(), 100);
            });
            await this.leaderboardClientConfig.saveLeaderboardClientConfig('navFavorites', values);
        },
        load: async () => {
            return (
                (await firstValueFrom(this.leaderboardClientConfig.navFavorites)) ||
                DEFAULT_NAV_FAVORITES
            );
        }
    };

    constructor(
        public appHeaderService: AppHeaderService,
        themeService: ThemeService,
        slide: SlideService,
        private router: Router,
        private navSection: NavSectionService,
        private clientState: ClientStateService,
        private leaderboardClientConfig: LeaderboardClientConfigService,
        private stateStream: StateStreamService,
        private prompt: PromptService,
        private windowTitle: Title,
        private supportRequestNotification: SupportRequestNotificationService
    ) {
        this.rebuild = this.clientState.authToken.pipe(map(() => {}));
        this.navEntries = this.clientState.authToken.pipe(
            map(authToken => this.getNavEntries(authToken?.accessLevel || AccessLevel.None))
        );
        let lastNavigationEvent: NavigationEnd | null;
        this.router.events
            .pipe(takeUntil(this.destroyed))
            .forEach(e => {
                if (e instanceof NavigationEnd) {
                    // If there is no fragment, the route actually changed then make sure that all slides are closed.
                    // e.g. when the user navigates away, or just reloads the current route by clicking
                    // on the nav bar.
                    if (
                        !e.url.includes('#') ||
                        (lastNavigationEvent &&
                            noFragment(lastNavigationEvent.url) !== noFragment(e.url))
                    ) {
                        slide.closeAll();
                    }
                    lastNavigationEvent = e;
                }

                function noFragment(url: string) {
                    return /^([^#]+)/.exec(url)?.[1] || url;
                }
            })
            .catch(logger.error);
        themeService.setActiveTheme(compileTheme(leaderboardTheme));
        supportRequestNotification.setupNotifications(stateStream.supportRequests);
    }

    getNavEntries(accessLevel: AccessLevel): Array<SectionNavEntry | EndpointNavEntry> {
        const lbNav = [{ icon: listOlIcon, name: 'Leaderboard', path: fullClientRoutes.root }];
        const adminNav = [{ name: 'Admin Panel', path: fullClientRoutes.adminPanel }];
        const playerNav = [
            {
                icon: ticketSimpleIcon,
                name: 'Support',
                items: [
                    {
                        name: 'My requests',
                        path: fullClientRoutes.supportRequestList
                    },
                    {
                        name: 'Request support',
                        path: fullClientRoutes.supportRequestCreate
                    }
                ]
            }
        ];
        for (const c of Object.values(SupportRequestCategory)) {
            if (!(c in SUPPORT_REQUEST_SEVERITY)) {
                throw new Error(`Missing severity level for category ${c}`);
            }
        }
        const sruSeverity = (sru: AnySupportRequestUpdate) =>
            SUPPORT_REQUEST_SEVERITY[sru.category];
        const srNotifications = combineLatest([
            this.stateStream.training,
            this.stateStream.supportRequestsWaiting
        ]).pipe(
            map(([{ id }, srus]) =>
                srus
                    .filter(({ sessionId }) => id === sessionId || sessionId === '')
                    .map(sru => ({
                        severity: sruSeverity(sru),
                        message: this.supportRequestNotification.supportRequestUpdateToString(sru)
                    }))
            ),
            takeUntil(this.destroyed),
            shareReplay({ refCount: true, bufferSize: 1 })
        );

        const connectionViolationNotifications = this.stateStream.connectionViolations.pipe(
            map(violations =>
                violations
                    .filter(v => !v.cleared)
                    .map(v => ({
                        severity: NavNotifyLevel.Critical,
                        message: `Team "${v.teamName}" connection violation`
                    }))
            ),
            takeUntil(this.destroyed),
            shareReplay({ refCount: true, bufferSize: 1 })
        );

        const adminSrNav: Array<SectionNavEntry | EndpointNavEntry> = [
            {
                icon: ticketSimpleIcon,
                name: 'Support',
                items: [
                    {
                        name: 'Requests',
                        path: fullClientRoutes.adminSupportRequestList,
                        notifications: srNotifications
                    },
                    {
                        name: 'Create request',
                        path: fullClientRoutes.adminSupportRequestCreate
                    }
                ]
            }
        ];

        const loginNav = [
            {
                icon: userIcon,
                name: 'Log In',
                items: [
                    { name: 'Administrator', path: fullClientRoutes.loginAdmin },
                    {
                        name: 'Operator',
                        path: fullClientRoutes.loginAdminOrReadonly
                    },
                    {
                        name: 'Player',
                        path: fullClientRoutes.loginPlayer,
                        pathExtras: {
                            queryParams: {
                                redir: `/${fullClientRoutes.supportRequestCreate}`
                            }
                        }
                    }
                ]
            }
        ];
        switch (accessLevel) {
            default:
                return [...loginNav, ...adminNav, ...playerNav];
            case AccessLevel.Viewer:
                return [...lbNav, ...loginNav];
            case AccessLevel.Player:
                return [...lbNav, ...playerNav];
            case AccessLevel.Readonly:
            case AccessLevel.Admin:
                return [
                    ...lbNav,
                    {
                        icon: toolboxIcon,
                        name: 'Admin',
                        items: [
                            ...adminNav,
                            {
                                name: 'Storyboard',
                                path: fullClientRoutes.storyBoard,
                                group: 'Storyboard'
                            },
                            {
                                name: 'Settings',
                                path: fullClientRoutes.storyBoardSettings,
                                group: 'Storyboard'
                            },
                            {
                                name: 'Targets',
                                path: fullClientRoutes.storyBoardTargets,
                                group: 'Storyboard'
                            },
                            {
                                name: 'Devices',
                                path: fullClientRoutes.storyBoardDevices,
                                group: 'Storyboard'
                            },
                            {
                                name: 'Webhooks',
                                path: fullClientRoutes.storyBoardWebhooks,
                                group: 'Storyboard'
                            },
                            {
                                name: 'Decorations',
                                path: fullClientRoutes.storyBoardDecorations,
                                group: 'Storyboard'
                            },
                            {
                                name: 'Team Countries',
                                path: fullClientRoutes.teamSummaryCountries,
                                group: 'Teams'
                            }
                        ]
                    },
                    {
                        name: 'Analysis',
                        icon: microscopeIcon,
                        items: [
                            {
                                name: 'Team Summary',
                                path: fullClientRoutes.teamList,
                                notifications: connectionViolationNotifications
                            },
                            { name: 'Historical Scores', path: fullClientRoutes.eventHistory }
                        ]
                    },
                    {
                        icon: headsetIcon,
                        name: 'Commentator',
                        items: [
                            { name: 'Teams', path: fullClientRoutes.commentatorInfo },
                            { name: 'Objectives', path: fullClientRoutes.commentatorObjectives },
                            { name: 'Player Assets', path: fullClientRoutes.playerAssetUpload }
                        ]
                    },
                    {
                        icon: stethoscopeIcon,
                        name: 'Diagnostics',
                        items: [
                            { name: 'Troubleshooting', path: fullClientRoutes.troubleshooting },
                            {
                                name: 'Team Debug',
                                path: fullClientRoutes.teamDebug,
                                notifications: connectionViolationNotifications
                            },
                            { name: 'Flags', path: fullClientRoutes.diagnosticsFlags },
                            { name: 'Logs', path: fullClientRoutes.logs },
                            { name: 'Client Tracking', path: fullClientRoutes.clientTrackingDebug },
                            { name: 'Maintenance', path: fullClientRoutes.maintenance }
                        ]
                    },
                    {
                        icon: shieldIcon,
                        name: 'Security',
                        items: [{ name: 'IP Restrictions', path: fullClientRoutes.ipRestrictions }]
                    },
                    {
                        icon: infoIcon,
                        name: 'Info',
                        items: [
                            { name: 'Documentation', path: fullClientRoutes.docs },
                            { name: 'Changelog', path: fullClientRoutes.changelog }
                        ]
                    },
                    ...adminSrNav
                ];
        }
    }

    logOut() {
        this.clientState.logOut();
        this.router.navigateByUrl('/').catch(logger.error);
    }

    ngAfterViewInit(): void {
        this.nav
            .pipe(takeUntil(this.destroyed))
            .forEach(nav =>
                this.navSection.setClickedSectionObservable(
                    nav.clickedSection.pipe(takeUntil(this.destroyed))
                )
            )
            .catch(logger.error);
        combineLatest([
            this.nav.pipe(switchMap(nav => nav.bestMatch)),
            this.stateStream.config,
            this.stateStream.eventMode
        ])
            .pipe(takeUntil(this.destroyed))
            .forEach(([nbm, c, em]) => {
                const currentPage = nbm?.label || '';
                const eventName = c.event;
                const emShort = {
                    [EventMode.Development]: 'DEV',
                    [EventMode.Production]: '',
                    [EventMode.Test]: 'TEST',
                    [EventMode.Unknown]: '???'
                }[em];
                const modeDesc = emShort ? `${emShort} ` : '';
                this.windowTitle.setTitle(
                    `${modeDesc}${window.location.hostname.replace(
                        '.fortidemo.fortinet.com',
                        ''
                    )} - ${currentPage} - ${eventName} - ${this.title}`
                );
            })
            .catch(logger.error);
        firstValueFrom(
            combineLatest([
                this.clientState.adminAccess.pipe(filter(truthy)),
                this.stateStream.eventMode.pipe(filter(m => m === EventMode.Unknown)),
                this.stateStream.training.pipe(filter(t => !t.running))
            ]).pipe(takeUntil(this.destroyed))
        )
            .then(() => {
                return this.prompt.open({
                    component: EventModePromptComponent
                });
            })
            .catch(logger.error);
    }
    ngOnDestroy(): void {
        this.destroyed.next();
        this.destroyed.complete();
    }
}
